import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import Constants from 'expo-constants'
import { Mail, Phone, MapPin } from '@/components/ui/icons'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { LegalSectionTitle, LegalSectionCard, LegalBody } from '@/components/ui/LegalSection'
import { useSemanticColors, SPACING } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { getClientConfig } from '@/lib/client/config'

/**
 * Imprint / company information screen.
 *
 * Required by App Store and Google Play for marketplace apps. Pre-incorporation
 * placeholder — once the SAS is registered with CNR, swap legal_status_body
 * for the registration number and date.
 */
export default function ImprintScreen() {
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const clientConfig = getClientConfig()
  const contactEmail = clientConfig.brand.contact.email ?? t('imprint.contact_email')
  const contactWhatsapp = clientConfig.brand.contact.whatsapp ?? t('imprint.contact_whatsapp')

  const appVersion = (Constants.expoConfig?.version as string | undefined) ?? 'dev'

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('imprint.title')} backTestID="imprint.back-button" />
      <ScrollView
        showsVerticalScrollIndicator={false}
        // One gutter for the whole screen. The shared LegalSection primitives
        // inset their own SPACING.md, while ScreenHeader (and every sibling
        // card in /profile) sits at SPACING.lg — so the paragraph, the three
        // cards and the version line all hung 8pt left of the back button
        // above them. The extra SPACING.sm here lands the body on SPACING.lg.
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: SPACING.sm,
          paddingBottom: insets.bottom + SPACING.lg,
        }}
        testID="imprint.scroll"
      >
        {/* The lede leads: it used to be the quietest paragraph on the page
            while the CNR asiento/libro/matrícula boilerplate below shouted in
            full-strength primary at the same size. */}
        <Text
          style={{
            fontSize: 16,
            color: colors.textPrimary,
            fontFamily: FONTS.body,
            paddingHorizontal: SPACING.md,
            marginTop: SPACING.md,
            lineHeight: 24,
          }}
        >
          {t('imprint.intro')}
        </Text>

        <LegalSectionTitle title={t('imprint.legal_status_title')} colors={colors} />
        <LegalSectionCard colors={colors}>
          {/* Reference data, not the headline — one step back from the address
              and contact lines, which are what a reader actually comes for. */}
          <Text
            style={{
              fontSize: 14,
              color: colors.textSecondary,
              fontFamily: FONTS.body,
              lineHeight: 20,
            }}
          >
            {t('imprint.legal_status_body')}
          </Text>
        </LegalSectionCard>

        <LegalSectionTitle title={t('imprint.address_title')} colors={colors} />
        <LegalSectionCard colors={colors}>
          {/* The address wraps to two lines, so a centred pin lands in the
              gutter between them. Top-aligned + 2pt down it optically centres
              on the first line, exactly like the CONTACTO rows below. */}
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}>
            <View style={{ marginTop: 2 }}>
              <MapPin size={18} color={colors.textSecondary} strokeWidth={1.8} />
            </View>
            <View style={{ flex: 1 }}>
              <LegalBody colors={colors}>{t('imprint.address_body')}</LegalBody>
            </View>
          </View>
        </LegalSectionCard>

        <LegalSectionTitle title={t('imprint.contact_title')} colors={colors} />
        <LegalSectionCard colors={colors}>
          <View style={{ gap: SPACING.sm }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
              <Mail size={18} color={colors.textSecondary} strokeWidth={1.8} />
              <LegalBody colors={colors}>{contactEmail}</LegalBody>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
              <Phone size={18} color={colors.textSecondary} strokeWidth={1.8} />
              <LegalBody colors={colors}>{contactWhatsapp}</LegalBody>
            </View>
          </View>
        </LegalSectionCard>

        {/* A real footer instead of a stray centred caption floating in the
            void under the last card: pushed to the bottom of the scroll frame
            by `marginTop: 'auto'`, ruled off, and on the same left gutter as
            every label above it. */}
        <View
          style={{
            marginTop: 'auto',
            marginHorizontal: SPACING.md,
            paddingTop: SPACING.md,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        >
          <Text
            style={{
              fontSize: 12,
              color: colors.textMuted,
              fontFamily: FONTS.body,
            }}
            testID="imprint.app-version"
          >
            {t('imprint.app_version', { version: appVersion })}
          </Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  )
}
